import { lazyWithRetry } from "@/lib/lazyWithRetry";
import {
  FileText,
  Users,
  ClipboardList,
  BarChart4,
  DollarSign,
  Presentation,
} from "lucide-react";
import { TabManager } from "@/components/project/tabs/TabManager";
import { FloatingSaveBar } from "@/components/project/FloatingSaveBar";
import {
  FinanceSaveBridgeProvider,
  useFinanceSaveBridge,
} from "@/components/project/finance/FinanceSaveBridge";
import { ExtendedProject } from "@/types/project";

// ============================================================================
// Types & Interfaces
// ============================================================================

// Using flexible types since these props are passed through to TabManager
// which already uses [key: string]: any
interface ProjectModalTabRendererProps {
  // Core state
  activeTab: string;
  tabLoaded: Record<string, boolean>;
  project: ExtendedProject | null;
  projectId: string;

  // Form state - using any for complex form types
  formValues: Record<string, any>;
  originalFormValues: Record<string, any>;
  hasUnsavedChanges: boolean;
  saving: boolean;
  formErrors: Record<string, string>;

  // UI state
  sidebarCollapsed: boolean;

  // Permissions
  hasPermission: (permission: string) => boolean;
  canEditProject: boolean;

  // User info
  currentUserId: string;
  userRoleId?: number | null;

  // Form handlers - using any for complex handler signatures
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleSelectChange: (name: string, value: any) => void;
  handleArrayChange: (name: string, value: string[]) => void;
  setFormValues: React.Dispatch<React.SetStateAction<any>>;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  handleSaveOverviewChanges: () => void;
  handleProjectUpdate: (updates: Partial<ExtendedProject>) => void;
  handleSubmit: () => void;
  updateFormDataFromModal: (updates: Record<string, any>) => void;

  // Document handlers - using any for complex signatures passed through to TabManager
  handleFileChange: (...args: any[]) => void;
  handleDrag: (...args: any[]) => void;
  handleDrop: (...args: any[]) => void;
  handleRemoveFile: (...args: any[]) => void;
  handleVerifyDriversLicense: () => void;
  handleVerifyUtilityBill: () => void;
  renderFilePreview: (...args: any[]) => React.ReactNode;

  // Document state - using any for complex state types
  documents: Record<string, any>;
  documentMetadata: Record<string, any>;
  dragActive: Record<string, boolean>;
  filePreviewUrls: Record<string, any>;
  fileLoading: Record<string, boolean>;
  uploadStatus: Record<string, any>;
  dlVerificationStatus: string;
  dlVerificationData: any;
  ubVerificationStatus: string;
  ubVerificationData: any;
  isVerifying: boolean;
  isVerifyingUb: boolean;

  // Lazy loading state
  documentCounts?: Record<string, number>;
  countsLoading?: boolean;
  loadedFields?: Set<string>;
  fetchFieldDocuments?: (projectId: string, fieldName: string) => Promise<void>;

  // Task state
  openTasks: any[];
  completedTasks: any[];
  assigneeNames: Record<string, string>;
  showCompletedTasks: boolean;
  setShowCompletedTasks: React.Dispatch<React.SetStateAction<boolean>>;
  setShowTaskForm: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedTask: React.Dispatch<React.SetStateAction<any>>;

  // Partner/Assignment handlers
  setShowChangeOwner: React.Dispatch<React.SetStateAction<boolean>>;
  setShowChangeManager: React.Dispatch<React.SetStateAction<boolean>>;
  setShowChangeProposalDesigner: React.Dispatch<React.SetStateAction<boolean>>;
  setShowChangeInstallerPartner: React.Dispatch<React.SetStateAction<boolean>>;
  setShowChangeInstallerPM: React.Dispatch<React.SetStateAction<boolean>>;
  setShowChangeSurveyor: React.Dispatch<React.SetStateAction<boolean>>;
  setShowChangeBizDev: React.Dispatch<React.SetStateAction<boolean>>;
  setShowChangeCloser: React.Dispatch<React.SetStateAction<boolean>>;
  setShowChangeSetter: React.Dispatch<React.SetStateAction<boolean>>;
  setProject: React.Dispatch<React.SetStateAction<ExtendedProject | null>>;

  // Modal callbacks
  handleClose: () => void;
  handleDelete: () => void;
  onClose: () => void;
  isOpen: boolean;
  onTaskSubmitted?: () => void;

  // Misc
  loading: boolean;
  derivedCommentTab?: string;
  projectManagementRefreshKey: number;
  documentUpdateTimestamp: number;
  hasLeftFormTab: React.MutableRefObject<boolean>;
}

// ============================================================================
// Subcomponents
// ============================================================================

interface TabHeaderProps {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  title: string;
  sidebarCollapsed: boolean;
}

function TabHeader({ icon: Icon, title, sidebarCollapsed }: TabHeaderProps) {
  return (
    <h3 className="text-lg font-medium mb-4 flex items-center text-foreground">
      <Icon
        className="h-5 w-5 min-w-[20px] text-primary"
        style={{
          marginRight: sidebarCollapsed ? 0 : "0.75rem",
        }}
      />
      {title}
    </h3>
  );
}

interface FloatingSaveButtonProps {
  hasUnsavedChanges: boolean;
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
}

/** Thin alias kept so the non-finance tabs read exactly as before the FIN-3.5 extraction. */
function FloatingSaveButton(props: FloatingSaveButtonProps) {
  return <FloatingSaveBar {...props} />;
}

/**
 * FIN-3.5 — the finance tab's save button. Merges two independent dirty states behind
 * one control: the project form values (useProjectForm) and the staged finance layout
 * (published up by FinanceSection through FinanceSaveBridge). Previously the layout
 * needed its own "Save layout" click before this button did anything useful.
 *
 * Layout first, then values: the layout save is the awaited one, and a failure inside
 * it surfaces in the editor's own error line rather than silently skipping the values.
 */
function FinanceFloatingSaveButton({ hasUnsavedChanges, saving, onCancel, onSave }: FloatingSaveButtonProps) {
  const finance = useFinanceSaveBridge();
  const financeStatus = finance?.status;
  const financeDirty = !!financeStatus?.active && !!financeStatus?.isDirty;

  return (
    <FloatingSaveBar
      hasUnsavedChanges={hasUnsavedChanges || financeDirty}
      saving={saving || !!financeStatus?.isSaving}
      saveTestId="finance-save-layout"
      onCancel={() => {
        if (financeStatus?.active) finance?.cancel();
        if (hasUnsavedChanges) onCancel();
      }}
      onSave={() => {
        void (async () => {
          if (financeDirty) await finance?.save();
          if (hasUnsavedChanges) onSave();
        })();
      }}
    />
  );
}

// ============================================================================
// Tab Import (Lazy loaded by TabManager, but some direct imports needed)
// ============================================================================

import { Suspense } from "react";
import { TabSkeleton } from "@/components/project/tabs/shared/TabSkeleton";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const ProjectNotesTab = lazyWithRetry(() => import("@/components/project/tabs/ProjectNotesTab"));
const SalesManagerInfoTab = lazyWithRetry(() => import("@/components/project/tabs/SalesManagerInfoTab").then(m => ({ default: m.SalesManagerInfoTab })));
const PartnerInfoTab = lazyWithRetry(() => import("@/components/project/tabs/PartnerInfoTab").then(m => ({ default: m.PartnerInfoTab })));
const WorkOrdersTab = lazyWithRetry(() => import("@/components/project/tabs/WorkOrdersTab").then(m => ({ default: m.WorkOrdersTab })));
const ProjectManagementTab = lazyWithRetry(() => import("@/components/project/tabs/ProjectManagementTab").then(m => ({ default: m.ProjectManagementTab })));
const SiteSurveyTab = lazyWithRetry(() => import("@/components/project/tabs/SiteSurveyTab").then(m => ({ default: m.SiteSurveyTab })));
const NewProjectFormTab = lazyWithRetry(() => import("@/components/project/tabs/NewProjectFormTab").then(m => ({ default: m.NewProjectFormTab })));

// ============================================================================
// Main Component
// ============================================================================

export function ProjectModalTabRenderer(props: ProjectModalTabRendererProps) {
  const {
    activeTab,
    tabLoaded,
    project,
    projectId,
    formValues,
    originalFormValues,
    hasUnsavedChanges,
    saving,
    formErrors,
    sidebarCollapsed,
    hasPermission,
    canEditProject,
    currentUserId,
    userRoleId,
    handleInputChange,
    handleSelectChange,
    handleArrayChange,
    setFormValues,
    setHasUnsavedChanges,
    handleSaveOverviewChanges,
    handleProjectUpdate,
    handleSubmit,
    updateFormDataFromModal,
    handleFileChange,
    handleDrag,
    handleDrop,
    handleRemoveFile,
    handleVerifyDriversLicense,
    handleVerifyUtilityBill,
    renderFilePreview,
    documents,
    documentMetadata,
    dragActive,
    filePreviewUrls,
    fileLoading,
    uploadStatus,
    dlVerificationStatus,
    dlVerificationData,
    ubVerificationStatus,
    ubVerificationData,
    isVerifying,
    isVerifyingUb,
    documentCounts,
    countsLoading,
    loadedFields,
    fetchFieldDocuments,
    openTasks,
    completedTasks,
    assigneeNames,
    showCompletedTasks,
    setShowCompletedTasks,
    setShowTaskForm,
    setSelectedTask,
    setShowChangeOwner,
    setShowChangeManager,
    setShowChangeProposalDesigner,
    setShowChangeInstallerPartner,
    setShowChangeInstallerPM,
    setShowChangeSurveyor,
    setShowChangeBizDev,
    setShowChangeCloser,
    setShowChangeSetter,
    setProject,
    handleClose,
    handleDelete,
    onClose,
    isOpen,
    onTaskSubmitted,
    loading,
    derivedCommentTab,
    projectManagementRefreshKey,
    documentUpdateTimestamp,
    hasLeftFormTab,
  } = props;

  const handleCancelChanges = () => {
    setFormValues(originalFormValues);
    setHasUnsavedChanges(false);
  };

  const hasManagementPermission = () =>
    hasPermission("manage_projects") ||
    hasPermission("project.manage_all") ||
    hasPermission("edit_projects") ||
    hasPermission("system.override") ||
    hasPermission("tenant.manage");

  // ============================================================================
  // Tab Rendering
  // ============================================================================

  // Opportunity Tab
  if (activeTab === "opportunity" && project) {
    return (
      <>
        <TabManager
          activeTab={activeTab}
          tabLoaded={tabLoaded}
          project={project}
          projectId={projectId}
          formValues={formValues}
          canEditProject={canEditProject}
          loading={loading}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          handleArrayChange={handleArrayChange}
          setFormValues={setFormValues}
          handleProjectUpdate={handleProjectUpdate}
          handleClose={handleClose}
          handleDelete={handleDelete}
          onSave={handleSaveOverviewChanges}
        />
        <FloatingSaveButton
          hasUnsavedChanges={hasUnsavedChanges}
          saving={saving}
          onCancel={handleCancelChanges}
          onSave={handleSaveOverviewChanges}
        />
      </>
    );
  }

  // Design/Documents Tab
  if (activeTab === "design" && tabLoaded.design) {
    return (
      <TabManager
        activeTab={activeTab}
        tabLoaded={tabLoaded}
        project={project}
        userRoleId={userRoleId}
        filePreviewUrls={filePreviewUrls}
        fileLoading={fileLoading}
        uploadStatus={uploadStatus}
        documents={documents}
        documentMetadata={documentMetadata}
        dragActive={dragActive}
        dlVerificationStatus={dlVerificationStatus}
        dlVerificationData={dlVerificationData}
        ubVerificationStatus={ubVerificationStatus}
        ubVerificationData={ubVerificationData}
        isVerifying={isVerifying}
        isVerifyingUb={isVerifyingUb}
        handleFileChange={handleFileChange}
        handleDrag={handleDrag}
        handleDrop={handleDrop}
        handleRemoveFile={handleRemoveFile}
        handleVerifyDriversLicense={handleVerifyDriversLicense}
        handleVerifyUtilityBill={handleVerifyUtilityBill}
        renderFilePreview={renderFilePreview}
        documentCounts={documentCounts}
        countsLoading={countsLoading}
        loadedFields={loadedFields}
        fetchFieldDocuments={fetchFieldDocuments}
      />
    );
  }

  // Notes Tab
  if (activeTab === "notes" && tabLoaded.notes) {
    return (
      <div>
        <TabHeader icon={FileText} title="Notes" sidebarCollapsed={sidebarCollapsed} />
        {project && (
          <Suspense fallback={<TabSkeleton />}>
            <ProjectNotesTab project={project} />
          </Suspense>
        )}
      </div>
    );
  }

  // New Project Form Tab
  if (activeTab === "new-project-form" && tabLoaded["new-project-form"] && !hasLeftFormTab.current) {
    return (
      <>
        <div className="flex-1 flex flex-col min-h-0 overflow-y-hidden" style={{ overflowX: 'clip' }}>
          {project && (
            <div className="flex-1 overflow-auto" style={{ overflowX: 'clip' }}>
              <Suspense fallback={<TabSkeleton />}>
                <NewProjectFormTab
                  formData={formValues as any}
                  updateFormData={updateFormDataFromModal}
                  handleFileChange={handleFileChange}
                  errors={formErrors}
                  pipelineId={formValues.pipeline_id || 0}
                  isSubmitting={saving}
                  onSubmit={handleSubmit}
                  projectId={projectId}
                  dragActive={dragActive}
                  handleDrag={handleDrag}
                  handleDrop={handleDrop}
                />
              </Suspense>
            </div>
          )}
        </div>
        <FloatingSaveButton
          hasUnsavedChanges={hasUnsavedChanges}
          saving={saving}
          onCancel={handleCancelChanges}
          onSave={handleSaveOverviewChanges}
        />
      </>
    );
  }

  // Sales Manager Tab
  if (activeTab === "sales-manager" && tabLoaded["sales-manager"]) {
    return (
      <div>
        <TabHeader icon={Users} title="Sales Team Assignments" sidebarCollapsed={sidebarCollapsed} />
        {project && (
          <Suspense fallback={<TabSkeleton />}>
            <SalesManagerInfoTab
              project={project}
              onProjectUpdate={(updatedProject) => {
                setProject(updatedProject);
                setFormValues((prev: Record<string, any>) => ({
                  ...prev,
                  ...updatedProject,
                }));
              }}
            />
          </Suspense>
        )}
      </div>
    );
  }

  // Partner Tab
  if (activeTab === "partner" && tabLoaded.partner) {
    return (
      <div>
        {project && (
          <Suspense fallback={<TabSkeleton />}>
            <PartnerInfoTab
              project={project}
              onProjectUpdate={handleProjectUpdate}
              setShowChangeOwner={setShowChangeOwner}
              setShowChangeManager={setShowChangeManager}
              setShowChangeProposalDesigner={setShowChangeProposalDesigner}
              setShowChangeInstallerPartner={setShowChangeInstallerPartner}
              setShowChangeInstallerPM={setShowChangeInstallerPM}
              setShowChangeSurveyor={setShowChangeSurveyor}
              setShowChangeBizDev={setShowChangeBizDev}
              setShowChangeCloser={setShowChangeCloser}
              setShowChangeSetter={setShowChangeSetter}
            />
          </Suspense>
        )}
      </div>
    );
  }

  // Work Orders Tab
  if (activeTab === "work-orders" && tabLoaded["work-orders"] && project) {
    return (
      <div>
        <TabHeader icon={ClipboardList} title="Work Orders" sidebarCollapsed={sidebarCollapsed} />
        <Suspense fallback={<TabSkeleton />}>
          <WorkOrdersTab project={project} currentUserId={currentUserId} />
        </Suspense>
      </div>
    );
  }

  // Project Tasks Tab
  if (activeTab === "project-tasks" && tabLoaded["project-tasks"]) {
    return (
      <TabManager
        activeTab={activeTab}
        tabLoaded={tabLoaded}
        project={project}
        openTasks={openTasks}
        completedTasks={completedTasks}
        assigneeNames={assigneeNames}
        showCompletedTasks={showCompletedTasks}
        setShowCompletedTasks={setShowCompletedTasks}
        setShowTaskForm={setShowTaskForm}
        setSelectedTask={setSelectedTask}
        currentUserId={currentUserId}
      />
    );
  }

  // Tasks/Project Management Tab
  if (activeTab === "tasks" && tabLoaded.tasks) {
    return (
      <div>
        <TabHeader icon={BarChart4} title="Project Management" sidebarCollapsed={sidebarCollapsed} />
        {project && hasManagementPermission() && (
          <Suspense fallback={<TabSkeleton />}>
            {/* Without a boundary, a render error in this tab unmounts the whole
                app and presents as a blank screen recoverable only by refresh. */}
            <ErrorBoundary>
            <ProjectManagementTab
              key={projectManagementRefreshKey}
              project={project}
              documentUpdateTimestamp={documentUpdateTimestamp}
              onProjectUpdate={(updatedProject) => {
                setFormValues((prev: Record<string, any>) => ({
                  ...prev,
                  ...updatedProject,
                }));
              }}
            />
            </ErrorBoundary>
          </Suspense>
        )}
        {project && !hasManagementPermission() && (
          <div className="p-4 text-center text-muted-foreground border border-border rounded-md bg-muted">
            <p>
              Project Management features are only available to Admin, Internal PM, Internal PM Manager, Biz Dev Rep, Biz Dev Manager, Commercial Sales Manager, and Finance roles.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Timeline Tab
  if (activeTab === "timeline" && tabLoaded.timeline) {
    return (
      <TabManager
        activeTab={activeTab}
        tabLoaded={tabLoaded}
        project={project}
      />
    );
  }

  // Site Survey Tab
  if (activeTab === "site-survey" && tabLoaded["site-survey"]) {
    return (
      <div>
        <TabHeader icon={ClipboardList} title="Site Survey" sidebarCollapsed={sidebarCollapsed} />
        {project && (
          <Suspense fallback={<TabSkeleton />}>
            <SiteSurveyTab
              project={{
                ...project,
                site_survey_completion_date:
                  formValues.site_survey_completion_date ||
                  project.site_survey_completion_date,
              }}
            />
          </Suspense>
        )}
      </div>
    );
  }

  // Book Consultation Tab
  if (activeTab === "book-consultation" && tabLoaded["book-consultation"]) {
    return (
      <TabManager
        activeTab={activeTab}
        tabLoaded={tabLoaded}
        project={project}
        projectId={project?.id || ""}
      />
    );
  }

  // Finance Tab
  if (activeTab === "finance" && tabLoaded.finance && project) {
    return (
      <FinanceSaveBridgeProvider>
        <TabManager
          activeTab={activeTab}
          tabLoaded={tabLoaded}
          project={project}
          currentUserId={currentUserId}
          formValues={formValues}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          handleFileChange={handleFileChange}
          handleDrag={handleDrag}
          handleDrop={handleDrop}
          handleRemoveFile={handleRemoveFile}
          dragActive={dragActive}
          filePreviewUrls={filePreviewUrls}
          uploadStatus={uploadStatus}
        />
        <FinanceFloatingSaveButton
          hasUnsavedChanges={hasUnsavedChanges}
          saving={saving}
          onCancel={handleCancelChanges}
          onSave={handleSaveOverviewChanges}
        />
      </FinanceSaveBridgeProvider>
    );
  }

  // Sales Manager Finance Tab
  if (activeTab === "sales-manager-finance" && tabLoaded["sales-manager-finance"] && project) {
    return (
      <TabManager
        activeTab={activeTab}
        tabLoaded={tabLoaded}
        project={project}
        formValues={formValues}
        handleInputChange={handleInputChange}
      />
    );
  }

  // Contracts Tab
  if (activeTab === "contracts" && tabLoaded["contracts"]) {
    return (
      <div>
        <TabHeader icon={FileText} title="Contracts" sidebarCollapsed={sidebarCollapsed} />
        {project ? (
          <TabManager
            activeTab={activeTab}
            tabLoaded={tabLoaded}
            project={project}
          />
        ) : (
          <p className="text-red-500">No project data available for Contracts tab.</p>
        )}
      </div>
    );
  }

  // Proposals Tab
  if (activeTab === "proposals" && tabLoaded["proposals"]) {
    return (
      <div>
        <TabHeader icon={Presentation} title="Proposals" sidebarCollapsed={sidebarCollapsed} />
        {project ? (
          <TabManager
            activeTab={activeTab}
            tabLoaded={tabLoaded}
            project={project}
          />
        ) : (
          <p className="text-red-500">No project data available for Proposals tab.</p>
        )}
      </div>
    );
  }

  // Lender Tab
  if (activeTab === "lender" && tabLoaded["lender"]) {
    return (
      <div>
        <TabHeader icon={DollarSign} title="Lender" sidebarCollapsed={sidebarCollapsed} />
        {project ? (
          <TabManager
            activeTab={activeTab}
            tabLoaded={tabLoaded}
            project={project}
          />
        ) : (
          <p className="text-red-500">No project data available for Lender tab.</p>
        )}
      </div>
    );
  }


  // Commercial Designs Tab
  if (activeTab === "commercial-designs" && tabLoaded["commercial-designs"]) {
    return (
      <TabManager
        activeTab={activeTab}
        tabLoaded={tabLoaded}
        project={project}
        formValues={formValues}
        onTaskSubmitted={onTaskSubmitted}
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  // Communication Tab
  if (activeTab === "communication" && tabLoaded.communication) {
    return (
      <div className="flex-1 flex flex-col min-h-0">
        {project && (
          <TabManager
            activeTab={activeTab}
            tabLoaded={tabLoaded}
            project={project}
            defaultCommentTab={derivedCommentTab}
          />
        )}
      </div>
    );
  }

  // SMS Tab
  if (activeTab === "sms" && tabLoaded.sms) {
    return (
      <div className="flex-1 flex flex-col min-h-0">
        {project && (
          <TabManager
            activeTab={activeTab}
            tabLoaded={tabLoaded}
            project={project}
          />
        )}
      </div>
    );
  }

  // Emails Tab
  if (activeTab === "emails" && tabLoaded.emails) {
    return (
      <div className="flex-1 flex flex-col min-h-0">
        {project && (
          <TabManager
            activeTab={activeTab}
            tabLoaded={tabLoaded}
            project={project}
            projectId={projectId}
          />
        )}
      </div>
    );
  }

  // Reports Tab
  if (activeTab === "reports" && tabLoaded.reports) {
    return (
      <div className="flex-1 flex flex-col min-h-0">
        {project && (
          <TabManager
            activeTab={activeTab}
            tabLoaded={tabLoaded}
            project={project}
            projectId={projectId}
          />
        )}
      </div>
    );
  }

  // Fallback for unknown tabs
  return null;
}

ProjectModalTabRenderer.displayName = "ProjectModalTabRenderer";
